<template>
  <v-container fluid class="relatorio-page">
    <BrAppBar>
      <h1 class="titulo">{{ $t('relatorios.title') }}</h1>
    </BrAppBar>

    <div class="filtros-row">
      <FiltroRangeDate
        :value="rangeDate"
        @input="onDateChange"
        :labelBtn="$t('common.period')"
        :actionLabels="{ apply: $t('common.ok') }"
        :btnProps="{ outlined: true }"
      />

      <v-select
        v-model="aggregateBy"
        :items="aggregateByOptions"
        item-text="text"
        item-value="value"
        dense
        outlined
        dark
        hide-details
        class="select-filtro select-aggregate"
      />

      <v-select
        v-model="groupBy"
        :items="groupByOptions"
        item-text="text"
        item-value="value"
        :label="$t('relatorios.groupByPlaceholder')"
        dense
        outlined
        dark
        hide-details
        clearable
        class="select-filtro select-groupby"
      />

      <Button variant="primary" @click="aplicarFiltros">{{ $t('common.apply') }}</Button>

      <v-btn icon dark class="btn-colunas" @click="colunasDialog = true">
        <v-icon>mdi-cog</v-icon>
      </v-btn>
    </div>

    <FiltrosAtivos :filtros="filtrosAtivosList" />

    <v-row class="mb-4">
      <v-col v-for="card in kpiCards" :key="card.label" cols="12" sm="4">
        <KpiCard v-bind="card" :loading="loadingTable" />
      </v-col>
    </v-row>

    <Card no-pad class="box-table box-table-gecom">
      <v-data-table
        dark
        :headers="visibleHeaders"
        :items="listaItens"
        :loading="loadingTable"
        hide-default-footer
        :items-per-page="-1"
        :mobile-breakpoint="0"
      >
        <template v-for="col in currencyColumns" v-slot:[`item.${col}`]="{ item }">
          {{ item[col] | currency }}
        </template>

        <template v-slot:item.period="{ item }">{{ formatPeriod(item.period) }}</template>

        <template v-slot:body.prepend>
          <tr class="total-row">
            <th>{{ $t('common.total') }}</th>
            <th v-for="col in visibleMetricColumns" :key="col">
              {{ countColumns.includes(col) ? (totalRow[col] || 0) : currency(totalRow[col]) }}
            </th>
          </tr>
        </template>
      </v-data-table>
    </Card>

    <v-dialog v-model="colunasDialog" max-width="360">
      <v-card class="modal-colunas">
        <v-card-title class="modal-colunas__title">{{ $t('relatorios.columnsSettings.title') }}</v-card-title>
        <v-divider />
        <v-card-text class="modal-colunas__body">
          <v-checkbox
            v-for="h in toggleableHeaders"
            :key="h.value"
            :label="h.text"
            :input-value="!hiddenColumns.includes(h.value)"
            @change="toggleColumn(h.value)"
            dense
            hide-details
            class="mb-2"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="colunasDialog = false">{{ $t('common.ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import { errorSnackbar } from '@/core/service/utils'
import { currency } from '@/core/filters/currency'

const HIDDEN_COLUMNS_KEY = 'afiliadoRelatorio.hiddenColumns'

export default {
  name: 'afiliadoRelatorioPage',
  components: {
    BrAppBar: () => import('@/views/layout/appBar'),
    FiltroRangeDate: () => import('@/views/components/filtroRangeDate'),
    Button: () => import('@/views/components/button'),
    Card: () => import('@/views/components/card'),
    KpiCard: () => import('@/views/components/kpiCard'),
    FiltrosAtivos: () => import('@/views/components/filtrosAtivos'),
  },
  data: () => ({
    rangeDate: {
      start: moment().startOf('month').format('YYYY-MM-DD'),
      end: moment().format('YYYY-MM-DD'),
    },
    appliedRange: {
      start: moment().startOf('month').format('YYYY-MM-DD'),
      end: moment().format('YYYY-MM-DD'),
    },
    aggregateBy: 'day',
    appliedAggregateBy: 'day',
    groupBy: null,
    appliedGroupBy: null,
    loadingTable: false,
    colunasDialog: false,
    hiddenColumns: [],
  }),
  computed: {
    ...mapGetters('afiliadosRelatorios', ['listaItens', 'totalRow', 'kpiCpa', 'kpiFtds', 'kpiRevshare']),

    aggregateByOptions () {
      return [
        { text: this.$t('relatorios.aggregateBy.day'), value: 'day' },
        { text: this.$t('relatorios.aggregateBy.week'), value: 'week' },
        { text: this.$t('relatorios.aggregateBy.month'), value: 'month' },
      ]
    },

    groupByOptions () {
      return [
        { text: this.$t('relatorios.groupBy.none'), value: null },
        { text: 'utm_campaign', value: 'utm_campaign' },
        { text: 'utm_medium', value: 'utm_medium' },
        { text: 'utm_source', value: 'utm_source' },
      ]
    },

    headers () {
      return [
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.period'), value: 'period' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.visits'), value: 'visits' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.registrations'), value: 'registrations' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.qftds'), value: 'qftds' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.ftds'), value: 'ftds' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.cpa'), value: 'cpa' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.revshare'), value: 'revshare' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.commissions'), value: 'commissions' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.subAffCom'), value: 'subAffCom' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.withdrawals'), value: 'withdrawals' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.netDeposit'), value: 'netDeposit' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.depositAmount'), value: 'depositAmount' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.ftdsAmount'), value: 'ftdsAmount' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.netPnl'), value: 'netPnl' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.adjustments'), value: 'adjustments' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.payments'), value: 'payments' },
        { align: 'start', class: 'table-header', text: this.$t('relatorios.columns.balance'), value: 'balance' },
      ]
    },

    // Plain-count columns — rendered as raw numbers, not currency
    countColumns () {
      return ['visits', 'registrations', 'qftds', 'ftds']
    },

    metricColumns () {
      return this.headers.filter(h => h.value !== 'period').map(h => h.value)
    },

    currencyColumns () {
      return this.metricColumns.filter(col => !this.countColumns.includes(col))
    },

    toggleableHeaders () {
      return this.headers.filter(h => h.value !== 'period')
    },

    visibleHeaders () {
      return this.headers.filter(h => !this.hiddenColumns.includes(h.value))
    },

    visibleMetricColumns () {
      return this.metricColumns.filter(col => !this.hiddenColumns.includes(col))
    },

    kpiCards () {
      return [
        { label: this.$t('painel.kpi.cpa'), value: this.currency(this.kpiCpa), icon: 'mdi-link' },
        { label: this.$t('painel.kpi.ftds'), value: this.kpiFtds || 0, icon: 'mdi-account-plus' },
        { label: this.$t('painel.kpi.revshare'), value: this.currency(this.kpiRevshare), icon: 'mdi-trending-up' },
      ]
    },

    filtrosAtivosList () {
      const aggregateLabel = this.aggregateByOptions.find(o => o.value === this.appliedAggregateBy)
      const groupLabel = this.groupByOptions.find(o => o.value === this.appliedGroupBy)
      const filtros = [
        {
          label: this.$t('common.period'),
          value: `${moment(this.appliedRange.start).format('DD/MM/YY')} - ${moment(this.appliedRange.end).format('DD/MM/YY')}`,
        },
        {
          label: this.$t('relatorios.aggregateByLabel'),
          value: aggregateLabel ? aggregateLabel.text : '',
        },
      ]
      if (this.appliedGroupBy) {
        filtros.push({ label: this.$t('relatorios.groupByLabel'), value: groupLabel ? groupLabel.text : this.appliedGroupBy })
      }
      return filtros
    },
  },
  mounted () {
    const stored = localStorage.getItem(HIDDEN_COLUMNS_KEY)
    if (stored) {
      try {
        this.hiddenColumns = JSON.parse(stored)
      } catch (e) {
        this.hiddenColumns = []
      }
    }
    this.aplicarFiltros()
  },
  methods: {
    ...mapActions('afiliadosRelatorios', ['getRelatorio']),
    currency,
    onDateChange (e) {
      this.rangeDate = { start: e.start, end: e.end }
    },
    aplicarFiltros () {
      this.appliedRange = { ...this.rangeDate }
      this.appliedAggregateBy = this.aggregateBy
      this.appliedGroupBy = this.groupBy
      this.buscar()
    },
    buscar () {
      this.loadingTable = true
      this.getRelatorio({
        dtStart: this.appliedRange.start,
        dtEnd: this.appliedRange.end,
        aggregateBy: this.appliedAggregateBy,
        groupBy: this.appliedGroupBy,
      })
        .catch(err => errorSnackbar(err.error))
        .finally(() => { this.loadingTable = false })
    },
    formatPeriod (period) {
      if (this.appliedAggregateBy === 'month') return moment(period).format('MMMM/YYYY')
      return moment(period).format('DD/MM/YYYY')
    },
    toggleColumn (value) {
      const idx = this.hiddenColumns.indexOf(value)
      if (idx > -1) {
        this.hiddenColumns.splice(idx, 1)
      } else {
        this.hiddenColumns.push(value)
      }
      localStorage.setItem(HIDDEN_COLUMNS_KEY, JSON.stringify(this.hiddenColumns))
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/components/table';

.titulo {
  color: #FFF;
}

.filtros-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  .select-filtro {
    max-width: 200px;

    ::v-deep .v-select__selection,
    ::v-deep .v-select__selections,
    ::v-deep input {
      color: #FFF !important;
    }

    ::v-deep .v-label {
      color: #C7C7D8 !important;
    }

    ::v-deep .v-icon {
      color: #C7C7D8 !important;
    }

    ::v-deep fieldset {
      border-color: #7C7C8A !important;
    }
  }
}

.box-table {
  .v-data-table-header-mobile {
    display: none;
  }

  ::v-deep .v-data-table {
    background: transparent !important;

    thead tr th {
      background: rgba(255, 255, 255, 0.04) !important;
      color: #7C7C8A !important;
      font-size: 11px !important;
      font-weight: 700 !important;
      letter-spacing: 0.7px !important;
      text-transform: uppercase !important;
      border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
      white-space: nowrap !important;

      &:first-child {
        position: sticky;
        left: 0;
        z-index: 3;
        background: #23232E !important;
        box-shadow: 4px 0 6px -4px rgba(0, 0, 0, 0.5);
      }
    }

    .v-data-table-header th.sortable .v-data-table-header__icon {
      color: var(--v-primary-base) !important;
    }

    tbody tr {
      border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;

      &:hover {
        background: rgba(255, 255, 255, 0.03) !important;

        td:first-child { background: #24242E !important; }
      }

      &:last-child td { border-bottom: none !important; }
    }

    tbody td {
      font-size: 13px !important;
      color: #C8C8D4 !important;
      border-bottom: none !important;
      padding-top: 14px !important;
      padding-bottom: 14px !important;
      white-space: nowrap !important;

      &:first-child {
        position: sticky;
        left: 0;
        z-index: 2;
        background: #1D1D29 !important;
        box-shadow: 4px 0 6px -4px rgba(0, 0, 0, 0.5);
      }
    }
  }
}

.total-row {
  background-color: #FFFFFF0D;
  color: #7C7C8A;

  th {
    font-size: 13px !important;
    font-weight: 700 !important;
    padding: 14px 16px !important;
    text-align: left;
    white-space: nowrap !important;

    &:first-child {
      position: sticky;
      left: 0;
      z-index: 2;
      background: #23232D !important;
      box-shadow: 4px 0 6px -4px rgba(0, 0, 0, 0.5);
    }
  }
}

.modal-colunas {
  &__title {
    color: #FFF;
    font-size: 16px;
    font-weight: 600;
  }

  &__body {
    max-height: 320px;
    overflow-y: auto;
  }
}
</style>
